import React from 'react';
import { IPublicAndPrivateSettings } from '@pepperhq/location-sdk';
import { Box, styled, Typography } from '@mui/material';
import Done from '@mui/icons-material/Done';
import Close from '@mui/icons-material/Close';

import { isDefined } from 'lib/typeguards';
import { MuiCardForm } from 'lib/form/MuiCardForm';
import { SettingDisplay } from 'lib/SettingDisplay';
import { SwitchFormField } from 'lib/form/fields/SwitchFormField';
import { TextFormField } from 'lib/form/fields/TextFormField';
import { PasswordFormField } from 'lib/form/fields/PasswordFormField';
import { SettingDisplayPassword } from '../SettingDisplayPassword';

const PREFIX = 'MarketingSettings';

const classes = {
    settingsCard: `${PREFIX}-settingsCard`,
    displayClassName: `${PREFIX}-displayClassName`
};

const StyledMuiCardForm = styled(MuiCardForm)(({ theme }) => ({
    [`&.${classes.settingsCard}`]: {
        marginBottom: theme.spacing(2)
    },
    [`& .${classes.displayClassName}`]: {
        padding: theme.spacing(2)
    }
}));

type IProps = {
    settings: IPublicAndPrivateSettings;
    onSubmit: (values: Record<string, any>) => Promise<boolean>;
    locationId?: string;
};

export function BookingsSettingsCard({ settings, onSubmit, locationId }: IProps) {
    const guestLineSettings = settings?.guestline;
    return (
        <StyledMuiCardForm
            title="Bookings"
            className={classes.settingsCard}
            boxClassName={classes.displayClassName}
            initialValues={{
                bookingsPollingEnabled: isDefined(guestLineSettings?.bookingsPollingEnabled)
                    ? guestLineSettings?.bookingsPollingEnabled
                    : false,
                interfaceId: guestLineSettings?.interfaceId,
                operatorCode: guestLineSettings?.operatorCode,
                password: guestLineSettings?.password,
                guestlineSiteId: guestLineSettings?.siteId
            }}
            onSubmit={onSubmit}
            childrenWhileDisplaying={
                <Box display="flex" flexDirection="column">
                    {/* Location Only */}
                    {locationId !== undefined && (
                        <SettingDisplay label="Site ID">
                            <Typography variant="body2">{guestLineSettings?.siteId}</Typography>
                        </SettingDisplay>
                    )}
                    {/* Global Only */}
                    {locationId === undefined && (
                        <SettingDisplay label="Booking Polling Enabled">
                            {guestLineSettings?.bookingsPollingEnabled && <Done />}
                            {!guestLineSettings?.bookingsPollingEnabled && <Close />}
                        </SettingDisplay>
                    )}
                    <SettingDisplay label="Interface ID">
                        <Typography variant="body2">{guestLineSettings?.interfaceId}</Typography>
                    </SettingDisplay>
                    <SettingDisplay label="Operator Code">
                        <Typography variant="body2">{guestLineSettings?.operatorCode}</Typography>
                    </SettingDisplay>
                    <SettingDisplayPassword
                        title="Password"
                        text={guestLineSettings?.password}
                        variant="body2"
                    />
                </Box>
            }
            childrenWhileEditing={
                <Box display="flex" flexDirection="column">
                    {/* Location Only */}
                    {locationId !== undefined && (
                        <SettingDisplay label="Site ID">
                            <TextFormField name="guestlineSiteId" />
                        </SettingDisplay>
                    )}
                    {/* Global Only */}
                    {locationId === undefined && (
                        <SettingDisplay label="Booking Polling Enabled">
                            <SwitchFormField name="bookingsPollingEnabled" />
                        </SettingDisplay>
                    )}
                    <SettingDisplay label="Interface ID">
                        <TextFormField type="number" name="interfaceId" />
                    </SettingDisplay>
                    <SettingDisplay label="Operator Code">
                        <TextFormField name="operatorCode" />
                    </SettingDisplay>
                    <SettingDisplay label="Password">
                        <PasswordFormField name="password" />
                    </SettingDisplay>
                </Box>
            }
        />
    );
}
